import React, { useMemo, lazy, Suspense, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { arrayOf, number } from 'prop-types';
import { contextPropTypes } from '../../../proptypes/shared';
import styles from './styles/styles.module.scss';
import { debounce } from 'lodash';
import SearchBar from '../SearchBar';
import useSearch from '../../../hooks/useSearch';
import { trackSearchResultClick } from '../../../utils/requests/algolia_requests';
import { DESKTOP_BREAKPOINT } from '../../../utils/constants/breakpoints';
import { ARROW_UP, ARROW_DOWN, ESC } from '../../../utils/constants/key_codes';

const MobileSearchBar = lazy(() => import('../MobileSearchBar'));

const HomeSearchBar = ({ latLng, windowWidth, context }) => {
  const searchContainer = useRef();
  const enableClickTracking = useMemo(() => {
    // click tracking is for loggedout users
    // and users that are neither admin or pro
    if (!context.currentUser) {
      return true;
    }

    return !(context.proUser || context.adminUser);
  }, [context]);

  const { searchState, updateQuery, updateActiveTabFilter, toggleEngaged, updateActiveIndexResult } = useSearch({
    latLng,
    languageRegionCode: context.languageRegionCode,
    enableClickTracking
  });

  const navigateToSearchResult = result => {
    window.location = result.url;
  };

  const handleSearchResultClick = useCallback(
    (result, idx) => {
      if (!enableClickTracking) {
        return navigateToSearchResult(result);
      }

      return trackSearchResultClick(result, idx + 1, searchState.queryID)
        .then(() => {
          // place these data points in local storage to be used for conversion metrics
          if (window && window.localStorage) {
            window.localStorage.setItem('algoliaQueryId', searchState.queryID);
            window.localStorage.setItem('algoliaObjectId', result.objectID);
          }
        })
        .catch(err => {
          console.error('error', err);
        })
        .finally(() => {
          navigateToSearchResult(result);
        });
    },
    [searchState.queryID]
  );

  const isMobileWidth = useMemo(() => {
    return windowWidth && windowWidth < DESKTOP_BREAKPOINT;
  }, [windowWidth]);

  const mobileSearchBar = useMemo(() => {
    return isMobileWidth && searchState.engaged;
  }, [isMobileWidth, searchState.engaged]);

  const checkForFocusOut = debounce(() => {
    if (searchContainer.current && !searchContainer.current.contains(document.activeElement)) {
      toggleEngaged(false);
    }
  }, 800);

  const handleKeyUp = e => {
    // this handles keyboard interactions, for arrow_up and down as well as esc
    if (window.keyboardUser) {
      checkForFocusOut();
    }

    if ([ARROW_UP, ARROW_DOWN].includes(e.keyCode)) {
      e.preventDefault();
    }

    switch (e.keyCode) {
      case ESC:
        toggleEngaged(false);
        break;
      case ARROW_UP:
        return searchState.activeIndexResult === 0
          ? updateActiveIndexResult(searchState.results.length - 1)
          : updateActiveIndexResult(searchState.activeIndexResult - 1);
      case ARROW_DOWN:
        return searchState.activeIndexResult + 1 === searchState.results.length
          ? updateActiveIndexResult(0)
          : updateActiveIndexResult(searchState.activeIndexResult + 1);
      default:
        return null;
    }
  };

  return (
    <>
      {mobileSearchBar &&
        ReactDOM.createPortal(
          <Suspense fallback={null}>
            <MobileSearchBar
              query={searchState.query}
              updateQuery={updateQuery}
              results={searchState.results}
              filters={searchState.filters}
              activeFilterIndex={searchState.activeFilterIndex}
              updateActiveTabFilter={updateActiveTabFilter}
              toggleEngaged={toggleEngaged}
              engaged={searchState.engaged}
              context={context}
              activeIndexResult={searchState.activeIndexResult}
              updateActiveIndexResult={updateActiveIndexResult}
              handleSearchResultClick={handleSearchResultClick}
            />
          </Suspense>,
          document.getElementById('modalPortal')
        )}
      <div ref={searchContainer} className={styles.container} onKeyUp={handleKeyUp}>
        <SearchBar
          query={searchState.query}
          updateQuery={updateQuery}
          results={searchState.results}
          filters={searchState.filters}
          activeFilterIndex={searchState.activeFilterIndex}
          updateActiveTabFilter={updateActiveTabFilter}
          toggleEngaged={toggleEngaged}
          engaged={searchState.engaged}
          context={context}
          activeIndexResult={searchState.activeIndexResult}
          updateActiveIndexResult={updateActiveIndexResult}
          handleSearchResultClick={handleSearchResultClick}
          mobileSearchBar={mobileSearchBar}
        />
      </div>
    </>
  );
};

HomeSearchBar.propTypes = {
  latLng: arrayOf(number),
  windowWidth: number,
  context: contextPropTypes.isRequired
};

HomeSearchBar.defaultProps = {
  latLng: null,
  windowWidth: null
};

export default HomeSearchBar;
